import React from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Form, message } from "antd";
import { API } from "api";
import { UTILS } from "utils";
import { LoginAction, SettingsAction, Language } from "redux/actions";
import { StreamRoomsPaths } from "features/byteplus/byteplus.routes";
import { authRoutesUrls } from "Layouts/AuthLayout/PublicRoutes";
import AppButton from "components/ui/AppButton";
import { CONSTANTS as C } from "config";
import AuthLayout from "../layout";
import { loginFields as Fields } from "../utils/fields";
import FieldRenderer from "../components/field-renderer";

const checkIfValidUser = (USER) => {
  const types = [
    C?.userTypes?.["BRAND-ADMIN"],
    C?.userTypes?.["MANAGER"],
    C?.userTypes?.["STREAM-ROOM-ACCESS"],
  ];
  if (types.includes(USER)) {
    return true;
  } else return false;
};

const Login = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const verifyAccount = (success) => {
    const data = {}
    data.email = success.data.user.email;
    data.baseLanguage = success.data.user.brandId.baseLanguage;
    data.name = success.data.user.userName;
    data.phoneNumber = success.data.user.phoneNumber;
    history.push(authRoutesUrls.verify, {
      stateRegister:
      {
        values: {
          ...data,
        }
      }
    });
  };

  const loginUser = (success) => {
    const userDetail = UTILS.getCurrentUser();
    dispatch(LoginAction.toogleLogin(true));
    localStorage.setItem("loginEvent", Date.now().toString());
    if (
      UTILS.isValidObject(["data", "user", "brandId", "baseLanguage"], success)
    ) {
      let obj = {
        direction:
          success?.data?.user?.brandId?.baseLanguage === "en" ? "ltr" : "rtl",
        lang: success?.data?.user?.brandId?.baseLanguage,
      };
      UTILS.saveLanguageInformation(obj);
      document
        .getElementsByTagName("html")[0]
        .setAttribute("dir", obj.direction);
      let clang = success?.data?.user?.brandId?.baseLanguage?.toUpperCase();
      dispatch(Language.updateLanguage(clang));
    }
    if (checkIfValidUser(success?.data?.user?.userType)) {
      UTILS.saveCurrentUser({
        ...userDetail,
        token: success.data.token,
      });
      const url =
        success?.data?.user?.userType === C?.userTypes?.["STREAM-ROOM-ACCESS"]
          ? StreamRoomsPaths.list
          : process.env.REACT_APP_COMPANY_NAME === C.COMPANY_NAMES.RESTHERO
            ? "/app/dashboard/onboarding"
            : "/app/dashboard";
      history.push(url);
    } else {
      message.error(t("invalid username or password"));
    }
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    if (data.phoneNumber.charAt(4) === "0") {
      data.phoneNumber = UTILS.formatPhoneNumber(data.phoneNumber);
    }
    const success = await API.SIGN_IN({
      phoneNumber: data.phoneNumber,
      password: data.password,
      userType: "BRAND-ADMIN",
      deviceType: "web",
      language: "EN",
    });
    if (success?.response === 200 && success?.success === 1 && success?.data) {
      UTILS.saveCurrentUser({
        name: success?.data?.user?.name,
        email: success?.data?.user?.email,
        phoneNumber: success.data.user?.phoneNumber,
        token: null,
        storeFrontURL: success.data?.storeFrontURL,
        storePreviewURL: success?.data?.storePreviewURL,
        brandId: success.data?.user?.brandId?._id,
        userId: success.data?.user?.userId,
        userType: success?.data?.user?.userType,
        brandName: success.data?.user.brandId?.brandName,
        subscribedPackage: success?.data?.user.subscribedPackage?.packageId
          ? success.data?.user?.subscribedPackage?.packageId
          : "",
        catalogueType: success?.data?.user?.brandId?.catalogueType,
        temporaryDomainName: success?.data?.brandSettings?.temporaryDomainName,
      });
      dispatch(
        SettingsAction.updateCatalogueType(
          success?.data?.user?.brandId?.catalogueType
        )
      );
      let obj = {
        direction:
          success?.data?.user?.brandId?.baseLanguage === "en" ? "ltr" : "rtl",
        lang: success?.data?.user?.brandId?.baseLanguage,
      };
      UTILS.saveLanguageInformation(obj);
      if (
        success?.response === 200 &&
        success?.success === 1 &&
        success?.data?.user?.isVerified
      ) {
        loginUser(success);
      } else if (
        success?.response === 200 &&
        success?.success === 1 &&
        !success?.data?.user?.isVerified
      ) {
        verifyAccount(success);
      }
    } else {
      message.error(success.message);
    }
    setLoading(false);
  };

  React.useEffect(() => {
    const hasErrors = form
      .getFieldsError()
      .some(({ errors }) => errors.length > 0);
    if (hasErrors) {
      form.validateFields().catch(() => { });
    }
  }, [i18n.language]);

  return (
    <AuthLayout
      title={t("Log In")}
      description={t("Welcome back, Please enter your details.")}
      loading={loading}
      isModified={form.isFieldsTouched() || loading}
      showBackButton={false}
    >
      <div className="flex-1 flex flex-col gap-4 justify-center h-full">
        <Form
          form={form}
          name="login-form-info"
          id={"login-form"}
          layout="vertical"
          autoComplete="off"
          initialValues={{
            phoneNumber: "",
            password: "",
          }}
          onFinish={handleSubmit}
        >
          {Fields(t).map((field) => (
            <Form.Item
              key={field.typeKey + "-" + field.label}
              name={field.typeKey}
              rules={field?.rules}
              {...(field.typeKey === "phoneNumber"
                ? { getValueFromEvent: (v) => v ?? "" }
                : {})}
            >
              <FieldRenderer field={field} />
            </Form.Item>
          ))}
          <div className="flex justify-end mb-10 font-semibold ">
            <Link
              to={authRoutesUrls.forgotPassword}
              className="text-blue-500 hover:text-blue-600 transition-all duration-300"
            >
              {t("Forgot Password?")}
            </Link>
          </div>
        </Form>


        <AppButton type="submit" form="login-form" className="w-full">
          {t("Login")}
        </AppButton>
        <div className="text-center text-slate-450 font-semibold">
          {t("Don't have an account?")}{" "}
          <Link
            to={authRoutesUrls.register}
            className="text-blue-500 hover:text-blue-600 transition-all duration-300 capitalize"
          >
            {t("Sign up")}
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;
